/* ==========================================================================
   Harper Reborn — cart extras (loaded on every page after cart-drawer.js)

   window.HP.cart  — the ONE place that talks to /cart/update.js for discount
                     codes. Used by the drawer/cart-page discount field and by
                     the product-page blocks (quantity breaks, click-to-copy code,
                     "birlikte al"). Keeps a queued code in sessionStorage that is
                     applied right after the next add-to-cart.
   <hp-cart-discount> — discount input + removable chips (snippets/hp-cart-discount.liquid)
   <hp-cart-timer>    — drawer urgency countdown; deadline kept in sessionStorage
                        so re-renders don't reset it
   [data-hp-upsell-nav="prev|next"] — arrows for the upsell strip (ara-bundle)

   Depends on Dawn globals: routes, fetchConfig, publish/subscribe, PUB_SUB_EVENTS,
   trapFocus, and the CartItems / CartDrawerItems custom elements.
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;
  var Q_KEY = 'hp_pending_discount';
  var A_KEY = 'hp_applied_discount';

  function ss(key, val) {
    try {
      if (val === undefined) return window.sessionStorage.getItem(key) || '';
      if (val === '' || val === null) window.sessionStorage.removeItem(key);
      else window.sessionStorage.setItem(key, val);
    } catch (e) {
      return '';
    }
    return val;
  }

  function norm(code) {
    return String(code || '').trim().toUpperCase();
  }

  function sectionInnerHTML(html, selector) {
    if (typeof html !== 'string' || html === '') return null;
    var parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector || '.shopify-section');
    return parsed ? parsed.innerHTML : null;
  }

  var cart = {
    /** The Dawn element whose getSectionsToRender() we mirror (drawer first, then cart page). */
    host: function () {
      return doc.querySelector('cart-drawer-items') || doc.querySelector('cart-items');
    },

    sections: function () {
      var host = cart.host();
      if (!host || typeof host.getSectionsToRender !== 'function') return null;
      try {
        return host.getSectionsToRender();
      } catch (e) {
        return null;
      }
    },

    /** POST /cart/update.js with the full set of codes ('' clears). Resolves with the cart JSON. */
    post: function (codes, withSections) {
      var body = { discount: (codes || []).map(norm).filter(Boolean).join(',') };
      var secs = withSections ? cart.sections() : null;
      if (secs) {
        body.sections = secs.map(function (s) { return s.section; }).join(',');
        body.sections_url = window.location.pathname;
      }
      var cfg = typeof fetchConfig === 'function' ? fetchConfig() : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
      cfg.body = JSON.stringify(body);
      return fetch(window.routes && window.routes.cart_update_url ? window.routes.cart_update_url : '/cart/update.js', cfg).then(function (r) {
        return r.json();
      });
    },

    /** Current cart JSON (GET /cart.js). */
    fetch: function () {
      return fetch((window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js', { headers: { Accept: 'application/json' } }).then(function (r) {
        return r.json();
      });
    },

    codesOf: function (json) {
      return ((json && json.discount_codes) || []).map(function (d) {
        return norm(d.code);
      }).filter(Boolean);
    },

    isApplicable: function (json, code) {
      code = norm(code);
      var hit = ((json && json.discount_codes) || []).filter(function (d) {
        return norm(d.code) === code;
      })[0];
      return !!(hit && hit.applicable !== false);
    },

    /** Paint the rendered sections from an update/change response (same loop as Dawn's cart.js). */
    render: function (json) {
      var secs = cart.sections();
      if (!secs || !json || !json.sections) return false;
      secs.forEach(function (s) {
        var root = doc.getElementById(s.id);
        if (!root) return;
        var target = root.querySelector(s.selector) || root;
        var html = sectionInnerHTML(json.sections[s.section], s.selector);
        if (html !== null) target.innerHTML = html;
      });
      var empty = json.item_count === 0;
      var drawer = doc.querySelector('cart-drawer');
      var footer = doc.getElementById('main-cart-footer');
      var host = cart.host();
      if (host) host.classList.toggle('is-empty', empty);
      if (drawer) drawer.classList.toggle('is-empty', empty);
      if (footer) footer.classList.toggle('is-empty', empty);
      return true;
    },

    notify: function (json) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        // 'cart-items' as source keeps CartItems from doing a redundant second fetch.
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: json });
      }
    },

    /**
     * Add one code on top of whatever is already on the cart. If Shopify reports it as
     * not applicable the code is rolled back so it never rides into checkout.
     * Resolves with { ok: bool, cart: json }.
     */
    add: function (code, opts) {
      opts = opts || {};
      code = norm(code);
      if (!code) return Promise.resolve({ ok: false });
      return cart.fetch().then(function (current) {
        var others = cart.codesOf(current).filter(function (c) {
          return c !== code && (!opts.replace || c !== norm(opts.replace));
        });
        return cart.post(others.concat([code]), true).then(function (json) {
          if (json.status || json.errors) throw json;
          if (!cart.isApplicable(json, code)) {
            // Nothing visible changed (the code never applied), so roll back without
            // re-rendering — that keeps the <hp-cart-discount> element (and its error) alive.
            return cart.post(others, false).then(function (rolled) {
              return { ok: false, cart: rolled };
            });
          }
          if (opts.render !== false) {
            cart.render(json);
            cart.notify(json);
          }
          return { ok: true, cart: json };
        });
      });
    },

    /** Remove one code (leaves every other code untouched). */
    remove: function (code, opts) {
      opts = opts || {};
      code = norm(code);
      return cart.fetch().then(function (current) {
        var rest = cart.codesOf(current).filter(function (c) {
          return c !== code;
        });
        return cart.post(rest, opts.render !== false).then(function (json) {
          if (opts.render !== false) {
            cart.render(json);
            cart.notify(json);
          }
          return { ok: true, cart: json };
        });
      });
    },

    /* ---- queue: a code chosen on the product page, applied after the next add ----
       queue('CODE')  apply CODE (replacing the one we applied earlier, if any)
       queue('-')     remove the code we applied earlier, add nothing
       queue('')      nothing to do after the next add                                   */
    queue: function (code) {
      ss(Q_KEY, norm(code));
    },
    queued: function () {
      return ss(Q_KEY);
    },
    applied: function () {
      return ss(A_KEY);
    },

    /** Called after an add-to-cart: swap our previously applied code for the queued one. */
    flush: function () {
      var next = cart.queued();
      var prev = cart.applied();
      if (!next) return Promise.resolve();
      ss(Q_KEY, '');
      if (next === '-') {
        ss(A_KEY, '');
        return prev ? cart.remove(prev) : Promise.resolve();
      }
      return cart.add(next, { replace: prev }).then(function (res) {
        ss(A_KEY, res.ok ? next : '');
        return res;
      });
    }
  };

  window.HP = window.HP || {};
  window.HP.cart = cart;

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!event || event.source === 'cart-items') return;
      if (!cart.queued()) return;
      // Let the drawer paint the add response first, then re-paint with the discount.
      setTimeout(function () {
        cart.flush().catch(function (e) {
          console.error('[hp-cart] discount', e);
        });
      }, 60);
    });
  }

  /* ---------------- <hp-cart-discount> ---------------- */
  var pending = null; // typed value survives the drawer's innerHTML swaps

  class HpCartDiscount extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-hp-discount-form]');
      this.input = this.querySelector('input');
      this.error = this.querySelector('[data-hp-discount-error]');
      this.details = this.querySelector('details');
      this.codes = (this.dataset.codes || '').split(',').map(norm).filter(Boolean);
      if (pending && Date.now() - pending.t < 10000) {
        if (this.input) this.input.value = pending.value;
        if (this.details && pending.open) this.details.open = true;
        if (this.error && pending.error) this.showError();
      }
      pending = null;
      if (this.form) {
        this.form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.apply(this.input ? this.input.value : '');
        });
      }
      if (this.input) {
        this.input.addEventListener('input', () => this.hideError());
      }
      this.querySelectorAll('[data-hp-remove-code]').forEach((btn) => {
        btn.addEventListener('click', () => this.removeCode(btn.dataset.hpRemoveCode));
      });
    }

    disconnectedCallback() {
      pending = {
        value: this.input ? this.input.value : '',
        open: !!(this.details && this.details.open),
        error: !!(this.error && !this.error.hidden),
        t: Date.now()
      };
    }

    busy(on) {
      this.classList.toggle('is-busy', !!on);
      this.querySelectorAll('button, input').forEach((el) => {
        el.disabled = !!on;
      });
    }

    showError(msg) {
      if (!this.error) return;
      if (msg) this.error.textContent = msg;
      this.error.hidden = false;
      this.classList.add('hp-disc--error');
      if (this.input) this.input.setAttribute('aria-invalid', 'true');
    }

    hideError() {
      if (!this.error) return;
      this.error.hidden = true;
      this.classList.remove('hp-disc--error');
      if (this.input) this.input.removeAttribute('aria-invalid');
    }

    apply(raw) {
      var code = norm(raw);
      if (!code || this.codes.indexOf(code) > -1) return;
      if (doc.querySelector('.cart__items--disabled')) return; // a quantity request is in flight
      var defaultMsg = this.error ? this.error.textContent : '';
      this.busy(true);
      cart
        .add(code)
        .then((res) => {
          if (!res.ok) {
            this.busy(false);
            this.showError(defaultMsg);
            return;
          }
          // The element has been re-rendered by cart.render(); refocus the new one.
          var drawer = doc.querySelector('cart-drawer');
          var fresh = doc.querySelector('hp-cart-discount summary, hp-cart-discount input');
          if (drawer && fresh && typeof trapFocus === 'function') trapFocus(drawer, fresh);
          else if (fresh) fresh.focus();
        })
        .catch(() => {
          this.busy(false);
          this.showError((window.cartStrings && window.cartStrings.error) || defaultMsg);
        });
    }

    removeCode(code) {
      this.busy(true);
      cart.remove(code).catch(() => this.busy(false));
    }
  }
  if (!customElements.get('hp-cart-discount')) customElements.define('hp-cart-discount', HpCartDiscount);

  /* ---------------- <hp-cart-timer> ---------------- */
  var T_KEY = 'hp_cart_timer_deadline';

  class HpCartTimer extends HTMLElement {
    connectedCallback() {
      this.out = this.querySelector('[data-hp-timer-value]') || this;
      var mins = parseInt(this.dataset.minutes || '12', 10);
      var secs = parseInt(this.dataset.seconds || '59', 10);
      var deadline = parseInt(ss(T_KEY) || '0', 10);
      if (!deadline || deadline < Date.now()) {
        deadline = Date.now() + (mins * 60 + secs) * 1000;
        ss(T_KEY, String(deadline));
      }
      this.deadline = deadline;
      this.tick();
      this.iv = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      if (this.iv) clearInterval(this.iv);
    }

    tick() {
      var left = Math.max(0, Math.floor((this.deadline - Date.now()) / 1000));
      var m = Math.floor(left / 60);
      var s = left % 60;
      this.out.textContent = (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s;
      if (left <= 0) {
        clearInterval(this.iv);
        this.classList.add('is-done');
      }
    }
  }
  if (!customElements.get('hp-cart-timer')) customElements.define('hp-cart-timer', HpCartTimer);

  // Reset the urgency timer when the cart empties so the next session starts fresh.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.cartData && event.cartData.item_count === 0) ss(T_KEY, '');
    });
  }

  /* ---------------- upsell strip arrows ---------------- */
  doc.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-hp-upsell-nav]');
    if (!btn) return;
    var strip = btn.closest('[data-hp-upsell]');
    strip = strip && strip.querySelector('[data-hp-upsell-strip]');
    if (!strip) return;
    var dir = btn.dataset.hpUpsellNav === 'prev' ? -1 : 1;
    strip.scrollBy({ left: dir * strip.clientWidth * 0.85, behavior: 'smooth' });
  });

  /* ---------------- cart page: consume a queued code (no drawer → product-form redirected here) ---------------- */
  function onReady() {
    if (!doc.querySelector('cart-items')) return;
    if (cart.queued()) cart.flush().catch(function () {});
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', onReady);
  else onReady();
})();
